import { useState, useEffect, useRef } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { STATISTICS_FIELDS } from "@/constants/segmentOptions";
import { useApp } from "@/contexts/AppContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = {
  id: "centerText",
  beforeDraw: (chart) => {
    const { width, height, ctx } = chart;
    ctx.restore();

    const fontSize = Math.min(width, height) / 20;
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#333";

    const text = `${chart.config.options.plugins.centerText.totalVasts}`;
    const subText = `Total VASTs`;

    const textX = Math.round((width - ctx.measureText(text).width) / 2);
    const textY = height / 2 - fontSize / 2;

    ctx.fillText(text, textX, textY);

    ctx.font = `${fontSize * 0.6}px Arial`;
    ctx.fillStyle = "#666";
    const subTextX = Math.round((width - ctx.measureText(subText).width) / 2);
    const subTextY = textY + fontSize * 0.8;

    ctx.fillText(subText, subTextX, subTextY);
    ctx.save();
  },
};

export default function CampaignFieldDistribution({
  campaignId,
  campaignName,
  days = 0,
}) {
  const { performRequest } = useApp();

  const [selectedField, setSelectedField] = useState("country");
  const [distribution, setDistribution] = useState([]);
  const [totalVasts, setTotalVasts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchFieldDistribution = async () => {
    if (!selectedField || !campaignId) return;

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        campaignId,
        field: selectedField,
      });

      if (days > 0) {
        params.append("days", days.toString());
      }

      const response = await performRequest(
        `/statistics/campaign-field-distribution?${params}`,
      );

      if (currentRequestId === requestIdRef.current) {
        setDistribution(response.distribution || []);
        setTotalVasts(response.totalVasts || 0);
        setLoading(false);
      }
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        console.error("Error fetching campaign field distribution:", err);
        setError(
          err.message || "Failed to fetch campaign field distribution data",
        );
        setDistribution([]);
        setTotalVasts(0);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (campaignId) {
      fetchFieldDistribution();
    }
  }, [campaignId, selectedField, days]);

  if (!campaignId) {
    return null;
  }

  return (
    <div className="statistics-chart-container">
      <div className="statistics-chart-header">
        <h5 className="statistics-chart-title">
          Campaign Analytics: {campaignName}
        </h5>
        <p className="statistics-chart-subtitle">
          Field distribution for this campaign
        </p>
      </div>
      <div className="statistics-chart-body">
        <div className="field-distribution-section">
          <div className="field-selector-header">
            <h6 className="section-title">Select Field</h6>
          </div>
          <div className="field-selector-container">
            <div className="field-selector">
              <label className="field-selector-label">Distribution Field</label>
              <select
                value={selectedField}
                onChange={(e) => setSelectedField(e.target.value)}
                className="chart-filter-select field-selector-input"
              >
                {STATISTICS_FIELDS.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <FieldDistributionView
          loading={loading}
          error={error}
          distribution={distribution}
          totalVasts={totalVasts}
          selectedField={selectedField}
        />
      </div>
    </div>
  );
}

/**
 * The distribution and its error/loading state.
 */
export function FieldDistributionView({
  loading,
  error,
  distribution,
  totalVasts,
  selectedField,
}) {
  const chartData = {
    labels: distribution.map((item) => item.label),
    datasets: [
      {
        data: distribution.map((item) => item.value),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
          "#E74C3C",
          "#2ECC71",
          "#F39C12",
          "#8E44AD",
          "#3498DB",
          "#E67E22",
        ],
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 40,
        left: 20,
        right: 20,
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 25,
          usePointStyle: true,
          font: {
            size: 12,
          },
          boxWidth: 15,
          boxHeight: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const percentage =
              totalVasts > 0
                ? ((context.parsed / totalVasts) * 100).toFixed(1)
                : 0;
            return [
              `${context.label}: ${context.parsed}`,
              `${percentage}% of total VASTs (${totalVasts})`,
            ];
          },
        },
      },
      centerText: {
        totalVasts,
      },
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: "#fff",
      },
    },
  };

  return (
    <>
      {error && (
        <div className="chart-error-state">
          <i className="fas fa-exclamation-triangle chart-error-icon"></i>
          <p>
            <strong>Error:</strong> {error}
          </p>
        </div>
      )}

      {loading && (
        <div className="chart-loading-state">
          <div className="chart-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>Loading chart data...</p>
        </div>
      )}

      {!loading && !error && distribution.length > 0 && (
        <div className="chart-section">
          <div className="chart-container">
            <div className="chart-canvas">
              <Pie
                data={chartData}
                options={chartOptions}
                plugins={[centerTextPlugin]}
              />
            </div>
          </div>
          <div className="chart-summary">
            <h6 className="summary-title">Field Distribution Summary</h6>
            <div className="summary-stats">
              <div className="summary-stat-card">
                <div className="summary-stat-body">
                  <small className="summary-stat-label">Selected Field</small>
                  <div className="summary-stat-value-text">
                    {STATISTICS_FIELDS.find((f) => f.value === selectedField)
                      ?.label || selectedField}
                  </div>
                </div>
              </div>
              <div className="summary-stat-card">
                <div className="summary-stat-body">
                  <small className="summary-stat-label">
                    Total unique devices
                  </small>
                  <div className="summary-stat-value">{totalVasts}</div>
                </div>
              </div>
            </div>
            <div className="summary-table-container">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Value</th>
                    <th>Count</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {distribution.map((item, index) => {
                    const totalPercentage =
                      totalVasts > 0
                        ? ((item.value / totalVasts) * 100).toFixed(1)
                        : 0;
                    return (
                      <tr key={index}>
                        <td className="summary-table-label" title={item.label}>
                          {item.label}
                        </td>
                        <td className="summary-table-value">{item.value}</td>
                        <td className="summary-table-percentage">
                          {totalPercentage}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && distribution.length === 0 && (
        <div className="chart-empty-state">
          <i className="fas fa-chart-pie chart-empty-icon"></i>
          <p>No data found for this campaign.</p>
        </div>
      )}
    </>
  );
}
